import L from 'leaflet';
import type { Location, PlatoonRef } from '../../store/realtimeStore';

// Neutral default map center. Override per-deployment via env; falls back to a
// generic point so no location is hardcoded into the build.
export const DEFAULT_MAP_CENTER: [number, number] = [
  Number((import.meta.env as any).VITE_MAP_DEFAULT_LAT) || 23.8,
  Number((import.meta.env as any).VITE_MAP_DEFAULT_LNG) || 90.4,
];
export const DEFAULT_MAP_ZOOM = 13;

// Geofence type palette.
//   HQ-planned areas: FARMBASE / TARGET / BOUNDARY / CHECKPOINT
//   Field-dropped live pins: HIDEOUT / RV
export const GEOFENCE_TYPE_COLORS: Record<string, string> = {
  FARMBASE: '#0e7490',
  TARGET: '#ef4444',
  BOUNDARY: '#a855f7',
  CHECKPOINT: '#3b82f6',
  HIDEOUT: '#f59e0b',
  RV: '#22c55e',
};
export const GEOFENCE_EMOJI: Record<string, string> = {
  FARMBASE: '🏕️',
  TARGET: '🎯',
  BOUNDARY: '🛑',
  CHECKPOINT: '🚧',
  HIDEOUT: '🕳️',
  RV: '📍',
};
export const FIELD_PIN_TYPES = ['HIDEOUT', 'RV'];

export type MapType = 'dark' | 'street' | 'satellite' | 'hybrid';

export const MAP_TILES: Record<MapType, { url: string; attribution: string; subdomains: string[] }> = {
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution:
      '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
    subdomains: ['a', 'b', 'c'],
  },
  street: {
    url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
  },
  satellite: {
    url: 'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
  },
  hybrid: {
    url: 'https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
  },
};

export interface MapFilters {
  showOnline: boolean;
  showOffline: boolean;
  showSOS: boolean;
  showGeofences: boolean;
  showTrails: boolean;
  // Operational overlay (F-1/F-2): farmbases + targets get their own toggles —
  // the Geofences toggle must NOT hide them.
  showFarmbases: boolean;
  showTargets: boolean;
  showInactiveTargets: boolean; // sub-toggle: eliminated/failed/expired/not-yet-active
}

export function hexToRgba(hex: string, alpha: number): string {
  const h = (hex || '').trim();
  if (!/^#?[0-9a-f]{6}$/i.test(h)) return `rgba(59, 130, 246, ${alpha})`; // fallback blue
  const cleaned = h.startsWith('#') ? h.slice(1) : h;
  const r = parseInt(cleaned.slice(0, 2), 16);
  const g = parseInt(cleaned.slice(2, 4), 16);
  const b = parseInt(cleaned.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

// Parse polygonCoords (string JSON or array of [lat,lon] / {lat,lon}) into positions.
export function parsePolygonCoords(raw: any): [number, number][] {
  try {
    const coords = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(coords)) return [];
    return coords
      .map((c: any) => (Array.isArray(c) ? [c[0], c[1]] : [c.lat, c.lon]))
      .filter((p: any) => typeof p[0] === 'number' && typeof p[1] === 'number') as [number, number][];
  } catch {
    return [];
  }
}

export function getPlatoon(loc: Location): PlatoonRef | undefined {
  return loc.platoon || loc.device?.platoon;
}

export const ACTIVE_SOS_STATUSES = ['TRIGGERED', 'ACKNOWLEDGED', 'RESPONDING'];

// Teardrop pin for geofences. Field-dropped pins (HIDEOUT / RV) get a dashed
// border to visually distinguish them from HQ-planned pins.
export const createGeofencePinIcon = (color: string, emoji: string, isFieldPin: boolean) => {
  return L.divIcon({
    className: 'geofence-pin-marker',
    html: `
      <div style="display: flex; flex-direction: column; align-items: center; filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));">
        <div style="
          width: 34px; height: 34px;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          background: ${color};
          border: 2px ${isFieldPin ? 'dashed' : 'solid'} white;
          display: flex; align-items: center; justify-content: center;">
          <span style="transform: rotate(45deg); font-size: 15px; line-height: 1;">${emoji}</span>
        </div>
      </div>
    `,
    iconSize: [34, 40],
    iconAnchor: [17, 38],
    popupAnchor: [0, -36],
  });
};
